import { CameraControls, Grid, Text } from "@react-three/drei";
import { Canvas, useThree } from "@react-three/fiber";
import CamControls from "camera-controls";
import Loader from "@src/components/Loader/Loader";
import Sprite from "@src/components/Sprite/Sprite";
import { useCharacterStore } from "@src/stores/characterStore";
import {
    setCharacterListVersionScales,
    toImperial,
    toImperialAsString,
} from "@src/utils/character-utils";
import { useQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useRef, useState } from "react";
import {
    CharacterListVersion,
    Preferences,
    ScaledCharacterListVersion,
} from "@src/types/global";
import { fetchUserPreferences } from "@src/services/requests/user";
type Sprite = { sprite: React.ReactNode; pos: number[] };
/**
 * Displays characters contained in the Stack one next to the other. The first character of the stack is used as a basis for scale.
 * TODO: Enable reordering the characters in the stack
 */

const charHeelAdjust = -0.1;
const HeightChartPage = () => {
    const [sprites, setSprites] = useState<Sprite[]>([]);
    const [units, setUnits] = useState<"metric" | "imperial">("metric");

    const cameraControlRef = useRef<CameraControls | null>(null);
    const characterList = useCharacterStore((state) => state.characterList);

    const { data: userPref, isFetching } = useQuery<Preferences>({
        queryKey: ["userPref"],
        queryFn: fetchUserPreferences,
        placeholderData: {
            measurement_unit: "metric",
            default_character_visibility: false,
            auto_grant_access_to_friends: false,
            color_scheme: "default",
        },
        staleTime: 60 * 1000,
    });
    useEffect(() => {
        if (userPref?.measurement_unit != null)
            setUnits(userPref.measurement_unit);
    }, [isFetching, userPref]);

    function setupSprite(ver: ScaledCharacterListVersion, index: number) {
        const translatedHeight =
            units == "metric"
                ? `${ver.height} cm`
                : toImperialAsString(ver.height);
        const posX = 1 + index * 2;
        // Calculer selon le nombre de personnages dans le stack, l'emplacement des Sprites en partant de la gauche et pas du centre (ou alors je centre la camera a gauche)
        return {
            sprite: (
                <group position={[posX, charHeelAdjust, 0]}>
                    <Sprite
                        position={[0, 0, 0]}
                        url={ver.body}
                        key={index}
                        scale={ver.scale}
                        index={index}
                        onClick={() => {}}
                    />
                    <Text position={[0, -1, 0]} scale={0.5}>
                        {translatedHeight}
                    </Text>
                </group>
            ),
            pos: [posX, 0],
        };
    }
    useEffect(() => {
        if (characterList.length > 0) {
            console.log("are we loopin captain");
            const scaledVersions = setCharacterListVersionScales(
                characterList,
                characterList[0],
            );
            setSprites(
                scaledVersions.map((ver, index) => {
                    return setupSprite(ver, index);
                }),
            );
        }
    }, [characterList, units]);

    if (characterList.length <= 0) {
        return (
            <div id="heightchart">
                <h1>HeightChart</h1>
                <p>You don't have characters in your stack !</p>
            </div>
        );
    }
    const restrictedControls = {
        left: CamControls.ACTION.TRUCK,
        middle: CamControls.ACTION.TRUCK,
        right: CamControls.ACTION.TRUCK,
        wheel: CamControls.ACTION.ZOOM,
    };
    const restrictedTouches = {
        one: CamControls.ACTION.TOUCH_TRUCK,
        two: CamControls.ACTION.TOUCH_TRUCK,
        three: CamControls.ACTION.TOUCH_TRUCK,
    };
    return (
        <div id="heightchart">
            <h1>HeightChart</h1>

            <div className="character-visualiser">
                <Suspense fallback={<Loader />}>
                    <Canvas camera={{ near: 0.00001, fov: 90 }}>
                        <HeightGrid
                            baseVersion={characterList[0]}
                            units={units}
                        />
                        <CameraControls
                            ref={cameraControlRef}
                            mouseButtons={restrictedControls}
                            touches={restrictedTouches}
                        />
                        <PlaceSprites sprites={sprites} />
                    </Canvas>
                </Suspense>
            </div>
        </div>
    );
};

const PlaceSprites = ({ sprites }: { sprites: Sprite[] }) => {
    const state = useThree();
    const vpWidth = state.viewport.width;

    return (
        <group position={[-vpWidth / 2 + 1, 0, 0]}>
            {sprites.map((s) => {
                return s.sprite;
            })}
        </group>
    );
};

/**
 * Based on user prefered untis, displays the heightchart behind characters
 * @returns a threejs fiber grid react node
 */
const HeightGrid = ({
    baseVersion,
    units = "metric",
}: {
    baseVersion: CharacterListVersion;
    units: "imperial" | "metric";
}) => {
    // based on the sprite's scale and character height, determine 1 meter or 1 ft
    const state = useThree();
    const vpWidth = state.viewport.width;

    let label = units == "metric" ? 100 : 1;
    let baseHeight = baseVersion.override_height || baseVersion.height || 0;

    let numUnit = 100;
    let cellDivider = 5;
    let baseSectionSize = 4; // Initially, a character of scale 1 is always contained within 4 grid squares
    let sectionSize =
        baseSectionSize - baseHeight / numUnit + charHeelAdjust / numUnit;
    let cellSize = sectionSize / cellDivider;
    //Section size is 4 - 180 / 100;
    if (units == "imperial") {
        numUnit = 12;
        const { feet, inches } = toImperial(baseHeight);
        const allInFeet = feet + inches / 12;
        cellDivider = 12;

        sectionSize = baseSectionSize / allInFeet + charHeelAdjust / numUnit;
        cellSize = 0;
    }
    const gridConfig = {
        cellSize: cellSize,
        cellThickness: 0.8,
        cellColor: "rgb(240, 240, 240)",
        sectionSize,
        sectionThickness: 1.5,
        sectionColor: "rgb(221, 227, 228)",
        fadeDistance: 100,
        fadeStrength: 1,
        followCamera: false,
        infiniteGrid: true,
    };
    const xRotation = (45 * Math.PI) / 2;
    let markings = [] as JSX.Element[];
    for (let i = 0; i < 100; i++) {
        markings.push(
            <Text
                position={[-vpWidth / 2 + 0.5, i * sectionSize + 0.1, 0]}
                scale={0.2}
            >
                {i * label} {units == "imperial" ? "ft" : "cm"}
            </Text>,
        );
    }
    return (
        <>
            <Grid
                rotation={[xRotation, 0, 0]}
                args={[10.5, 10.5]}
                position={[0, 0, 0]}
                {...gridConfig}
            />
            {markings.map((m) => m)}
        </>
    );
};
export default HeightChartPage;
